import {
  Calendar,
  CheckboxGroup,
  DropdownField,
  IOption,
  Label,
  RadioGroup,
  Switch,
  TextField,
} from "@/DependenciesV9";
import {
  OperatorFilterNumberEnums,
  OperatorFilterStringEnums,
} from "@/DependenciesV9/DataTable/Enums/TableEnum.ts";
import {
  defaultOperator,
  defaultValueFilter,
} from "@/DependenciesV9/DataTable/Filter";
import { IFormFilterProps } from "@/DependenciesV9/DataTable/Filter/FormFilter/Model.ts";
import {
  CalendarMultiWrapper,
  FormFilterWrapper,
  FormItemWrapper,
  HeaderItemWrapper,
  IconActionWrapper,
  SwitchMultiWrapper,
} from "@/DependenciesV9/DataTable/Filter/FormFilter/Style.ts";
import { ETypeColumn } from "@/DependenciesV9/DataTable/Filter/Model.ts";
import {
  OptionOnSelectData,
  SelectionEvents,
  SwitchOnChangeData,
} from "@fluentui/react-components";
import {
  ChevronDown20Regular,
  ChevronLeft20Regular,
  Dismiss20Regular,
} from "@fluentui/react-icons";
import { RadioGroupOnChangeData } from "@fluentui/react-radio";
import { cloneDeep } from "lodash";
import { ChangeEvent, FormEvent, JSX, useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { IColumnDl, IFilterQuery } from "../../Model";

const fakeOptions: IOption[] = [
  { value: "true", text: "True" },
  { value: "false", text: "False" },
  { value: "3", text: "Option 3" },
  { value: "4", text: "Option 4" },
];

const FormFilter = (props: IFormFilterProps) => {
  const { t } = useTranslation();
  const [isCollapsed, setIsCollapsed] = useState<boolean>(false);
  const [filterQuery, setFilterQuery] = useState<IFilterQuery | null>(null);
  const [isMultiDate, setIsMultiDate] = useState(false);

  useEffect(() => {
    if (
      props.filterQuery?.key !== filterQuery?.key ||
      props.filterQuery?.operator !== filterQuery?.operator ||
      props.filterQuery?.value !== filterQuery?.value
    ) {
      setFilterQuery(props.filterQuery ? cloneDeep(props.filterQuery) : null);
    }
  }, [props.filterQuery]);

  const onCollapsedItem = (): void => {
    setIsCollapsed(!isCollapsed);
  };

  const onSelectColumn = (
    _event: SelectionEvents,
    data: OptionOnSelectData
  ) => {
    if (
      props.selectedColumns &&
      !props.selectedColumns.includes(data?.optionValue || "")
    ) {
      const filterClone = cloneDeep(filterQuery);
      if (filterClone) {
        const indexColumn: number | undefined = (
          props.columnsDefault || []
        ).findIndex((column: IColumnDl) => column.key === data?.optionValue);
        if (
          indexColumn !== undefined &&
          props.columnsDefault &&
          props.columnsDefault[indexColumn]
        ) {
          filterClone.key = props.columnsDefault[indexColumn].key || "";
          filterClone.childQuery =
            props.columnsDefault[indexColumn].queryKey || "";
          filterClone.value = defaultValueFilter(
            props.columnsDefault[indexColumn].data
          );
          filterClone.operator = defaultOperator(
            props.columnsDefault[indexColumn].data
          );
          setFilterQuery(filterClone);
          if (props.onChange) {
            props.onChange(filterClone, props.indexFilter);
          }
        }
      }
    }
  };

  const onSelectOption = (
    _event: SelectionEvents,
    data: OptionOnSelectData
  ) => {
    const filterClone = cloneDeep(filterQuery);
    if (filterClone) {
      filterClone.operator = data.optionValue || "";
      setFilterQuery(filterClone);
      if (props.onChange) {
        props.onChange(filterClone, props.indexFilter);
      }
    }
  };

  const onChangeText = (
    _event: React.ChangeEvent<HTMLInputElement>,
    newValue?: string
  ) => {
    const filterClone = cloneDeep(filterQuery);
    if (filterClone) {
      filterClone.value = newValue || "";
      setFilterQuery(filterClone);
      if (props.onChange) {
        props.onChange(filterClone, props.indexFilter);
      }
    }
  };

  const onChangeNumber = (
    _event: React.ChangeEvent<HTMLInputElement>,
    newValue?: string
  ) => {
    const filterClone = cloneDeep(filterQuery);
    if (filterClone) {
      filterClone.value = Number(newValue || "0");
      setFilterQuery(filterClone);
      if (props.onChange) {
        props.onChange(filterClone, props.indexFilter);
      }
    }
  };

  const onChangeRadio = (
    _ev: FormEvent<HTMLDivElement>,
    data: RadioGroupOnChangeData
  ) => {
    const filterClone = cloneDeep(filterQuery);
    if (filterClone) {
      filterClone.value = data?.value || "";
      setFilterQuery(filterClone);
      if (props.onChange) {
        props.onChange(filterClone, props.indexFilter);
      }
    }
  };

  const onChangeCheckboxGroup = (values: string[]) => {
    const filterClone = cloneDeep(filterQuery);
    if (filterClone) {
      filterClone.value = values;
      setFilterQuery(filterClone);
      if (props.onChange) {
        props.onChange(filterClone, props.indexFilter);
      }
    }
  };

  const onChangeSwitch = (_ev: ChangeEvent<HTMLInputElement>, data: SwitchOnChangeData) => {
    setIsMultiDate(data.checked);
  }

  const onSelectDate = (date: Date, selectedDateRangeArray: Date[]) => {
    if(isMultiDate) {
      const filterClone = cloneDeep(filterQuery);
      if (filterClone && selectedDateRangeArray.length === 2) {
        filterClone.value = (selectedDateRangeArray || []).map((item) => new Date(item));
        setFilterQuery(filterClone);
        if (props.onChange) {
          props.onChange(filterClone, props.indexFilter);
        }
      }
    } else {
      const filterClone = cloneDeep(filterQuery);
      if (filterClone) {
        filterClone.value = new Date(date);
        setFilterQuery(filterClone);
        if (props.onChange) {
          props.onChange(filterClone, props.indexFilter);
        }
      }
    }
  };


  const renderTypeString = () => {
    const optionsString: IOption[] = [
      { value: OperatorFilterStringEnums.Equal, text: t("Equal") },
      {
        value: OperatorFilterStringEnums.NotEqual,
        text: t("Does not equal"),
      },
      { value: OperatorFilterStringEnums.Contain, text: t("Contains") },
      {
        value: OperatorFilterStringEnums.NotContain,
        text: t("Does not contain"),
      },
    ];
    return (
      <>
        {filterQuery ? (
          <>
            <FormItemWrapper className="FormItemWrapper">
              <DropdownField
                theme={props.theme}
                label={t("Filter option")}
                options={optionsString}
                selectedOptions={[filterQuery.operator]}
                onSelectOption={onSelectOption}
              ></DropdownField>
            </FormItemWrapper>
            <FormItemWrapper className="FormItemWrapper">
              <TextField
                label={t("Value")}
                theme={props.theme}
                value={filterQuery.value}
                onChangeText={onChangeText}
              ></TextField>
            </FormItemWrapper>
          </>
        ) : null}
      </>
    );
  };

  const renderTypeBoolean = () => {
    return filterQuery ? (
      <FormItemWrapper className="FormItemWrapper">
        <RadioGroup
          theme={props.theme}
          options={fakeOptions}
          value={filterQuery.value}
          onChange={onChangeRadio}
        ></RadioGroup>
      </FormItemWrapper>
    ) : null;
  };

  const renderTypeBooleanMulti = () => {
    return filterQuery ? (
      <FormItemWrapper className="FormItemWrapper">
        <CheckboxGroup
          theme={props.theme}
          options={fakeOptions}
          values={filterQuery.value}
          onChange={onChangeCheckboxGroup}
        ></CheckboxGroup>
      </FormItemWrapper>
    ) : null;
  };

  const renderTypeDate = () => {
    const optionsCalendarSingle: IOption[] = [
      { value: OperatorFilterNumberEnums.Equal, text: t("Equal") },
      { value: OperatorFilterNumberEnums.Less, text: t("Less than") },
      { value: OperatorFilterNumberEnums.Greater, text: t("Greater than") },
      {
        value: OperatorFilterNumberEnums.LessOrEqual,
        text: t("Less than or equal to"),
      },
      {
        value: OperatorFilterNumberEnums.GreaterOrEqual,
        text: t("Greater than or equal to"),
      },
    ];
    const optionsCalendarMultiple: IOption[] = [
      { value: OperatorFilterStringEnums.Contain, text: t("Within") },
      {
        value: OperatorFilterStringEnums.NotContain,
        text: t("Not within"),
      },
    ];
    return filterQuery ? (
      <>
        <FormItemWrapper className="FormItemWrapper">
          <DropdownField
            theme={props.theme}
            label={t("Filter option")}
            options={isMultiDate ? optionsCalendarMultiple : optionsCalendarSingle}
            selectedOptions={[filterQuery.operator]}
          ></DropdownField>
          <CalendarMultiWrapper className="CalendarMultiWrapper">
            <SwitchMultiWrapper className="SwitchMultiWrapper">
              <Switch
                checked={isMultiDate}
                label={t("Choose multiple days")}
                labelPosition="before"
                onChange={onChangeSwitch}
                theme={props.theme}
              />
            </SwitchMultiWrapper>
            <Calendar
              isDayPickerVisible={true}
              showGoToToday={true}
              isMonthPickerVisible={false}
              showMonthPickerAsOverlay={true}
              highlightSelectedMonth={true}
              allFocusable={true}
              theme={props.theme}
              selectedDate={filterQuery.value}
              onChange={onSelectDate}
              isMultiple={isMultiDate}
            />
          </CalendarMultiWrapper>
        </FormItemWrapper>
      </>
    ) : null;
  };

  const renderTypeNumber = () => {
    const optionsNumber: IOption[] = [
      { value: OperatorFilterNumberEnums.Equal, text: t("Equal") },
      { value: OperatorFilterNumberEnums.Less, text: t("Less than") },
      { value: OperatorFilterNumberEnums.Greater, text: t("Greater than") },
      {
        value: OperatorFilterNumberEnums.LessOrEqual,
        text: t("Less than or equal to"),
      },
      {
        value: OperatorFilterNumberEnums.GreaterOrEqual,
        text: t("Greater than or equal to"),
      },
    ];
    return (
      <>
        <FormItemWrapper className="FormItemWrapper">
          <DropdownField
            theme={props.theme}
            label={t("Filter option")}
            options={optionsNumber}
          ></DropdownField>
          <TextField
            label={t("Value")}
            theme={props.theme}
            type="number"
            onChange={onChangeNumber}
          ></TextField>
        </FormItemWrapper>
      </>
    );
  };

  const renderSelectFilterOption = (type: ETypeColumn): JSX.Element | null => {
    switch (type) {
      case ETypeColumn.STRING:
        return renderTypeString();
      case ETypeColumn.BOOLEAN:
      case ETypeColumn.BOOLEAN_STRING:
        return renderTypeBoolean();
      case ETypeColumn.BOOLEAN_MULTIPLE:
        return renderTypeBooleanMulti();
      case ETypeColumn.DATE:
        return renderTypeDate();
      case ETypeColumn.NUMBER:
        return renderTypeNumber();
      default:
        return null;
    }
  };

  return (
    <FormFilterWrapper className="FormFilterWrapper">
      <HeaderItemWrapper className="HeaderItemWrapper">
        <div className="header__content">
          <Label className="header__title">{`${t("Filter")}`}</Label>
        </div>
        <hr />
      </HeaderItemWrapper>
      <IconActionWrapper className="IconActionWrapper">
        {isCollapsed ? (
          <ChevronLeft20Regular onClick={() => onCollapsedItem()} />
        ) : (
          <ChevronDown20Regular onClick={() => onCollapsedItem()} />
        )}
        <Dismiss20Regular />
      </IconActionWrapper>
      {!isCollapsed && filterQuery ? (
        <>
          <DropdownField
            label={t("Select column")}
            options={props.options || []}
            selectedOptions={[filterQuery.key]}
            onSelectOption={onSelectColumn}
          ></DropdownField>
          {renderSelectFilterOption(
            (props.workingColumn?.data as ETypeColumn) || ""
          )}
        </>
      ) : null}
    </FormFilterWrapper>
  );
};

export default FormFilter;
